import React, { useState, useCallback, useEffect, useRef } from "react";
import { ThemeProvider } from "@mui/material/styles";
import {
  Grid,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableBody,
  Tooltip,
  Autocomplete,
  ListItemText,
  Typography,
  Chip,
  Card,
} from "@mui/material";
import {
  StyledTableCellHistory,
  StyledTableRowHistory,
  StyledTextField,
  StyledButton,
  OutlinedIconFab,
  theme,
  StyledCheckbox,
} from "../StyledComponents";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardVoiceIcon from "@mui/icons-material/KeyboardVoice";
import MicOffIcon from "@mui/icons-material/MicOff";
import ClearIcon from "@mui/icons-material/Clear";
import { durationType } from "../initialData/initialData";
import { formatDurationUnitForDisplay } from "../HistoriesData/durationUtils";
import Icon from "../Utils/Icons";
import { MedicationsTable } from "../Sections/MedicationsTable";
const MedicationsSimplified = ({
  activeSection,
  handleCrossButtonClick,
  data,
  setData,
  reasonsOptions,
  routeAutoData,
  initialObservationData,
  startRecording,
  stopRecording,
  isAiEnabled,
}) => {
  const [listeningOn, setListeningOn] = useState(false);
  const [newMedicine, setNewMedicine] = useState({
    id: null,
    term: "",
    selectedAdministration: null,
    reasons: [],
    additionalInstructions: "",
    time: "",
    when: "",
    frequency: "",
    dosage: "",
    duration: "",
    medicineNote: "",
  });

  const [medicineName, setMedicineName] = useState("");
  const [durationInputValue, setDurationInputValue] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [medicines, setMedicines] = useState(data);
  const [validationErrors, setValidationErrors] = useState({
    id: null,
    term: "",
    selectedAdministration: null,
    reasons: "",
    additionalInstructions: "",
    duration: "",
    medicineNote: "",
  });
  const timeOptions = [
    "5 mins",
    "10 mins",
    "15 mins",
    "20 mins",
    "30 mins",
    "45 mins",
    "1 hr",
    "Immediately",
  ];
  const whenOptions = [
    "Before Food",
    "After Food",
    "Before Breakfast",
    "After Breakfast",
    "Before Lunch",
    "Before Dinner",
    "After Dinner",
    "With Food",
    "With Tea",
    "Empty Stomach",
    "Bed Time",
    "Before Bath",
    "After Bath",
    "Before Sun",
    "After Sun",
    "SoS",
  ];
  const frequencyOptions = [
    "Daily",
    "Alternate Day",
    "Weekly",
    "Weekly Twice",
    "Weekly Thrice",
    "Fort Night",
    "Monthly",
    "Mon",
    "Tue",
    "Wed",
    "Thu",
    "Fri",
    "Sat",
    "Sun",
    "Mon, Wed",
  ];
  const dosageOptions = [
    "1 - 0 - 0",
    "0 - 0 - 1",
    "1 - 0 - 1",
    "1 - 1 - 1",
    "1 - 1 - 0",
    "0 - 1 - 0",
    "0 - 1 - 1",
    "0 - 0 - 0 - 1",
  ];

  useEffect(() => {
    setMedicines(data);
  }, [data]);

  const getDurationParts = (durationValue) => {
    const normalized = String(durationValue || "").trim();
    if (!normalized) return { value: "", unit: "", combined: "" };
    const valueMatch = normalized.match(/\d+/);
    const unitMatch = normalized
      .toLowerCase()
      .match(/\b(minutes?|hours?|days?|weeks?|months?|years?)\b/);
    return {
      value: valueMatch ? valueMatch[0] : "",
      unit: unitMatch ? unitMatch[1] : "",
      combined: normalized,
    };
  };

  const validateFields = () => {
    const errors = {};

    // Validate Medicine Name
    if (!newMedicine.term && medicineName.length === 0) {
      errors.term = "Medicine Name is a mandatory field.";
    }
    // Additional Instructions is optional.

    // Validate Duration
    // if (!newMedicine.duration) {
    //   errors.duration = "Duration is a mandatory field.";
    // } else
    if (durationInputValue && !newMedicine.duration) {
      errors.duration = "Please select a duration option.";
    }

    const durNum = Number(getDurationParts(newMedicine?.duration).value);
    if (newMedicine?.duration !== "" && newMedicine?.duration != null && durNum < 0) {
      errors.duration = "Duration must not be a negative number.";
    }

    setValidationErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleAdd = async () => {
    if (!validateFields()) {
      return; // Exit if validation fails
    }

    let newDetails = { ...newMedicine };

    // Add the new term to the complaints list
    let updatedDetails = [...medicines, newDetails];
    console.log("Diagnosis Updated Details", updatedDetails);
    setMedicines(updatedDetails);
    setNewMedicine({
      id: null,
      term: "",
      selectedAdministration: null,
      reasons: [],
      additionalInstructions: "",
      time: "",
      when: "",
      frequency: "",
      dosage: "",
      duration: "",
      medicineNote: "",
    });
    setDurationInputValue("");
    setValidationErrors({}); // Clear errors after successful submission
    setMedicineName("");
    setData(updatedDetails);
  };

  const handleInputChange = (field, value) => {
    let newDetails;

    if (field === "term") {
      newDetails = {
        ...newMedicine,
        [field]: value.term,
        id: value.id,
      };
    } else {
      newDetails = {
        ...newMedicine,
        [field]: value,
      };
    }

    setNewMedicine(newDetails);
  };

  // Function to handle row edit
  const handleEditRow = (index) => {
    setMedicineName(medicines[index].term);
    const row = medicines[index];
    const durationText = getDurationParts(row.duration).combined;
    setDurationInputValue(
      getDurationParts(durationText).value
    );
    setNewMedicine({ ...row, id: null, duration: durationText });
    setEditingIndex(index);
    // handleDeleteRow(index); // Remove the row from the list temporarily
  };
  console.log("Editing Index", editingIndex);
  const handleUpdate = async () => {
    if (!validateFields()) {
      return; // Exit if validation fails
    }

    let newDetails = { ...newMedicine, source: undefined };

    let updatedDetails = medicines.map((data, i) =>
      i == editingIndex ? newDetails : data
    );
    setMedicines(updatedDetails);
    setEditingIndex(null);
    setNewMedicine({
      id: null,
      term: "",
      selectedAdministration: null,
      reasons: [],
      additionalInstructions: "",
      time: "",
      when: "",
      frequency: "",
      dosage: "",
      duration: "",
      medicineNote: "",
    });
    setDurationInputValue("");
    setValidationErrors({}); // Clear errors after successful submission
    setMedicineName("");
    setData(updatedDetails);
  };
  // Function to handle row delete
  const handleDeleteRow = (index) => {
    let updatedDetails = medicines.filter((_, i) => i !== index);
    setMedicines(updatedDetails);
    setData(updatedDetails);
  };

  const handleClearName = () => {
    setValidationErrors({});
    setMedicineName("");
    setNewMedicine({
      ...newMedicine,
      term: "", // Use computed property name to set the field dynamically
    });
    setDurationInputValue("");
  };

  const handleConditionTyped = (value) => {
    setMedicineName(value);
    handleInputChange("term", { term: value, id: null });
  };

  console.log("Medicines data", medicines);
  console.log("Reasons", reasonsOptions);
  return (
    <ThemeProvider theme={theme}>
      {activeSection && (
        <>
          <Card
            sx={{
              borderRadius: "10px",
              boxShadow: "0 4px 4px 0 #C7C7CC inset",
              backgroundColor: "#FFF",
              backgroundImage: "url(/newImages/medicationsBackground.svg)",
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <Grid item xs={12} sm={12} md={12}>
              <Grid
                container
                spacing={2}
                sx={{
                  marginTop: "5px",
                  padding: "10px",
                }}
              >
                <Grid item xs={12} sm={7} md={6} sx={{ order: -10 }}>
                  <StyledTextField
                    value={medicineName}
                    onChange={(e) => {
                      handleConditionTyped(e.target.value);
                    }}
                    InputProps={{
                      endAdornment: (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <div style={{ marginRight: "5px" }}>
                            <Tooltip title="Clear">
                              <OutlinedIconFab
                                size="small"
                                variant="contained"
                                onClick={handleClearName}
                              >
                                <Icon name="clearTextField" size={32} />
                              </OutlinedIconFab>
                            </Tooltip>
                          </div>

                          {/* <>
                            {isAiEnabled &&
                              (listeningOn === false ? (
                                <Tooltip title="Start Listening">
                                  <OutlinedIconFab
                                    onClick={() => {
                                      startRecording();
                                      setListeningOn(true);
                                    }}
                                    variant="contained"
                                    color="defaultColor"
                                    size="small"
                                  >
                                    <Icon name="micNotInUse" size={32} />
                                  </OutlinedIconFab>
                                </Tooltip>
                              ) : (
                                <Tooltip title="Stop Listening">
                                  <OutlinedIconFab
                                    onClick={() => {
                                      // SpeechRecognition.stopListening();
                                      stopRecording("Medications");
                                      setListeningOn(false);
                                    }}
                                    variant="outlined"
                                    color="defaultColor"
                                    size="small"
                                  >
                                    <Icon name="micInUse" size={32} />
                                  </OutlinedIconFab>
                                </Tooltip>
                              ))}
                          </> */}
                        </div>
                      ),
                    }}
                    label="Medicine Name"
                    fullWidth
                    error={!!validationErrors.term} // Highlight field in red if there's an error
                    helperText={validationErrors.term} // Show error message
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} sx={{ order: -4 }}>
                  <StyledTextField
                    fullWidth
                    label="Additional Instructions"
                    error={!!validationErrors.additionalInstructions}
                    helperText={validationErrors.additionalInstructions}
                    value={newMedicine.additionalInstructions}
                    onChange={(e) =>
                      handleInputChange("additionalInstructions", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={6} sm={3} md={2} sx={{ order: -9 }}>
                  <Autocomplete
                    freeSolo
                    options={timeOptions}
                    value={newMedicine.time || ""}
                    onChange={(event, newValue) =>
                      handleInputChange("time", newValue || "")
                    }
                    onInputChange={(event, newInputValue) =>
                      handleInputChange("time", newInputValue)
                    }
                    renderInput={(params) => (
                      <StyledTextField
                        {...params}
                        label="Time"
                        placeholder="Select or type time"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6} sm={3} md={2} sx={{ order: -8 }}>
                  <Autocomplete
                    freeSolo
                    options={whenOptions}
                    value={newMedicine.when || ""}
                    onChange={(event, newValue) =>
                      handleInputChange("when", newValue || "")
                    }
                    onInputChange={(event, newInputValue) =>
                      handleInputChange("when", newInputValue)
                    }
                    renderInput={(params) => (
                      <StyledTextField
                        {...params}
                        label="When"
                        placeholder="Select or type when"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6} sm={3} md={2} sx={{ order: -7 }}>
                  <Autocomplete
                    freeSolo
                    options={frequencyOptions}
                    value={newMedicine.frequency || ""}
                    onChange={(event, newValue) =>
                      handleInputChange("frequency", newValue || "")
                    }
                    onInputChange={(event, newInputValue) =>
                      handleInputChange("frequency", newInputValue)
                    }
                    renderInput={(params) => (
                      <StyledTextField
                        {...params}
                        label="Frequency"
                        placeholder="Select or type frequency"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6} sm={3} md={3} sx={{ order: -6 }}>
                  <Autocomplete
                    freeSolo
                    options={dosageOptions}
                    value={newMedicine.dosage || ""}
                    onChange={(event, newValue) =>
                      handleInputChange("dosage", newValue || "")
                    }
                    onInputChange={(event, newInputValue) =>
                      handleInputChange("dosage", newInputValue)
                    }
                    renderInput={(params) => (
                      <StyledTextField
                        {...params}
                        label="Dosage"
                        placeholder="Select or type dosage"
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3} sx={{ order: -5 }}>
                  <Autocomplete
                    freeSolo={false}
                    options={
                      durationInputValue
                        ? durationType.map(
                          (unit) =>
                            `${durationInputValue} ${formatDurationUnitForDisplay(unit.value)}`
                        )
                        : []
                    }
                    value={
                      newMedicine.duration || null
                    }
                    inputValue={
                      newMedicine.duration || durationInputValue
                    }
                    open={
                      /^\d+$/.test(durationInputValue) &&
                      !newMedicine.duration &&
                      durationInputValue.length > 0
                    }
                    onChange={(_, selectedValue) => {
                      if (!selectedValue) return;
                      const parts = selectedValue.split(" ");
                      const numericPart = parts[0] || "";
                      setDurationInputValue(numericPart);
                      setNewMedicine((prev) => ({
                        ...prev,
                        duration: selectedValue,
                      }));
                      setValidationErrors((prev) => ({ ...prev, duration: "" }));
                    }}
                    onInputChange={(_, value, reason) => {
                      if (reason === "reset") return;
                      if (value === "") {
                        setDurationInputValue("");
                        setNewMedicine((prev) => ({
                          ...prev,
                          duration: "",
                        }));
                        return;
                      }
                      const numericPrefixMatch = value.match(/^\d+/);
                      if (numericPrefixMatch) {
                        const numericPart = numericPrefixMatch[0];
                        setDurationInputValue(numericPart);
                        setNewMedicine((prev) => ({
                          ...prev,
                          duration: "",
                        }));
                        return;
                      }
                      if (/^\d+$/.test(value)) {
                        setDurationInputValue(value);
                        setNewMedicine((prev) => ({
                          ...prev,
                          duration: "",
                        }));
                      }
                    }}
                    renderInput={(params) => (
                      <StyledTextField
                        {...params}
                        label="Duration"
                        fullWidth
                        error={!!validationErrors.duration}
                        helperText={validationErrors.duration}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={5} sm={6} md={2}>
                  <Autocomplete
                    options={routeAutoData} // Predefined options for selection
                    value={newMedicine.selectedAdministration} // Bind to the selected value
                    onChange={
                      (event, newValue) =>
                        handleInputChange("selectedAdministration", newValue) // Update state on selection change
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value?.id
                    } // Define equality check
                    renderInput={(params) => (
                      <StyledTextField
                        {...params}
                        label="Administration"
                        placeholder="Select administration method"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={5} md={3}>
                  <Autocomplete
                    multiple
                    limitTags={1}
                    options={reasonsOptions} // Predefined list of options
                    value={newMedicine.reasons} // Bind selected values
                    onChange={(event, newValue) => {
                      handleInputChange("reasons", newValue); // Store only the 'condition' values
                    }}
                    renderInput={(params) => (
                      <StyledTextField {...params} label="Reasons" fullWidth />
                    )}
                  />
                </Grid>

                <Grid item xs={8} sm={5} md={3}>
                  <StyledTextField
                    fullWidth
                    label="Notes"
                    value={newMedicine.medicineNote}
                    onChange={(e) =>
                      handleInputChange("medicineNote", e.target.value)
                    }
                  />
                </Grid>
                <Grid
                  item
                  xs={2}
                  md={1}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <StyledButton
                    bgColor="#0E3F66"
                    onClick={editingIndex != null ? handleUpdate : handleAdd}
                    variant="contained"
                    sx={{
                      fontSize: { xs: "0.8rem", sm: "1rem" },
                      width: "100%",
                    }}
                  >
                    {editingIndex != null ? "Update" : "Add"}
                  </StyledButton>
                </Grid>
              </Grid>
            </Grid>
          </Card>
        </>
      )}
      {medicines && (
        <MedicationsTable
          data={medicines}
          activeSection={activeSection}
          handleEditRow={handleEditRow}
          handleDeleteRow={handleDeleteRow}
        />
      )}
    </ThemeProvider>
  );
};

export default MedicationsSimplified;
